/**
 * F 340 — the Operator transport.
 *
 * F 331's AI Engine is a DASHBOARD: it describes which models are alive and
 * what they can do. It makes no model calls, so "use the AI Engine" cannot mean
 * "call engineState". This module is the thin call layer the chat needs.
 *
 * WHY response_format MATTERS. Measured against the three live providers for
 * F 340: groq/openai/gpt-oss-120b and nvidia/z-ai/glm-5.3 both returned a
 * bare parseable JSON object. gemini-3-flash-preview returned PROSE and no JSON
 * unless its instruction arrived as `systemInstruction` — sent as an ordinary
 * message it answered "which platform are you using?". So Gemini gets its
 * instruction through `systemInstruction` and the other two through `messages`.
 * That is a measured per-transport difference, not a preference.
 *
 * The API key never enters this module's return value and is never stored.
 */
import { operatorSystemPrompt, parseToolCall, type ParsedToolCall } from './tools';

export interface OperatorRequest {
  provider: 'groq' | 'nvidia' | 'gemini';
  model: string;
  apiKey: string;
  /** The user's message, plus any prior turns as plain text. */
  transcript: string;
}

export interface OperatorReply {
  ok: boolean;
  text: string;
  call: ParsedToolCall | null;
  status: number;
  latencyMs: number;
  error?: string;
}

async function timed<T>(fn: () => Promise<T>): Promise<{ ms: number; value: T }> {
  // Measure AFTER the await. `{ms: Date.now()-t, value: await fn()}` reads the
  // clock first and reports 0 for every call — the F 338/F 339 defect.
  const started = Date.now();
  const value = await fn();
  return { ms: Date.now() - started, value };
}

/** One Operator call. Returns the text AND the parsed tool call, if any. */
export async function callOperator(req: OperatorRequest): Promise<OperatorReply> {
  const system = operatorSystemPrompt();

  let url: string;
  let headers: Record<string, string>;
  let body: unknown;

  if (req.provider === 'gemini') {
    url = `https://generativelanguage.googleapis.com/v1beta/models/${req.model}:generateContent`;
    headers = { 'x-goog-api-key': req.apiKey, 'Content-Type': 'application/json' };
    body = {
      // Measured: Gemini ignores the instruction unless it arrives here.
      systemInstruction: { parts: [{ text: system }] },
      contents: [{ role: 'user', parts: [{ text: req.transcript }] }],
      generationConfig: {
        maxOutputTokens: 2000,
        temperature: 0,
        thinkingConfig: { thinkingBudget: 0 },
      },
    };
  } else if (req.provider === 'nvidia') {
    url = 'https://integrate.api.nvidia.com/v1/chat/completions';
    headers = { Authorization: `Bearer ${req.apiKey}`, 'Content-Type': 'application/json' };
    body = {
      model: req.model,
      messages: [
        { role: 'system', content: system },
        { role: 'user', content: req.transcript },
      ],
      max_tokens: 800,
      temperature: 0,
      response_format: { type: 'json_object' },
    };
  } else {
    url = 'https://api.groq.com/openai/v1/chat/completions';
    headers = { Authorization: `Bearer ${req.apiKey}`, 'Content-Type': 'application/json' };
    body = {
      model: req.model,
      messages: [
        { role: 'system', content: system },
        { role: 'user', content: req.transcript },
      ],
      max_tokens: 800,
      temperature: 0,
      response_format: { type: 'json_object' },
    };
  }

  try {
    const { ms, value: res } = await timed(async () => {
      const r = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
      const json = await r.json().catch(() => ({}));
      return { status: r.status, json };
    });

    let text = '';
    const j = res.json as Record<string, any>;
    if (Array.isArray(j?.candidates)) {
      const parts = j.candidates[0]?.content?.parts ?? [];
      text = parts.map((p: { text?: string }) => p.text || '').join('');
    } else if (Array.isArray(j?.choices)) {
      const msg = j.choices[0]?.message ?? {};
      text = msg.content || msg.reasoning_content || j.choices[0]?.text || '';
    }

    const error = res.status !== 200
      ? String(j?.error?.message ?? j?.detail ?? `HTTP ${res.status}`).slice(0, 160)
      : undefined;

    return {
      ok: res.status === 200 && text.trim().length > 0,
      text,
      call: parseToolCall(text),
      status: res.status,
      latencyMs: ms,
      error,
    };
  } catch (e) {
    return {
      ok: false,
      text: '',
      call: null,
      status: 0,
      latencyMs: 0,
      error: String((e as Error)?.message ?? e).slice(0, 160),
    };
  }
}
